import { motion, useViewportScroll, useTransform, useAnimation, AnimatePresence } from "framer-motion";
import React, { useRef, useLayoutEffect, useState, useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Layout, Row, Col } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import "./ProductCreationProcess.scss";
import ToolsInfo from "./ToolsInfo";
import ToolsInfoMobile from "./ToolsInfoMobile";
import { deviceType } from "../../../Utilities";
const { Content } = Layout;
gsap.registerPlugin(ScrollTrigger);

const product_creation_flow = [
    {
        img: "https://d48f7equ64qjl.cloudfront.net/static_files/homepage_assets/feature_1.png",
        top_text: "Your powerful all-in-one platform",
        title: "Create image-to-3D models and lifestyles in minutes",
        tags: [
            { ai: true, text: "AI Creator", beta: true },
            { text: "Try it now", link: true, link_src: "/ai-creator" }
        ]
    },
    {
        img: "https://d48f7equ64qjl.cloudfront.net/static_files/homepage_assets/feature_2.png",
        top_text: "Your powerful all-in-one platform",
        title: "QA models with your team and generate 4k assets",
        tags: [
            { text: "3D Model Creation" },
            { text: "Product Image Generator" }
        ]
    },
    {
        img: "https://d48f7equ64qjl.cloudfront.net/static_files/homepage_assets/feature_3.png",
        top_text: "Your powerful all-in-one platform",
        title: "Design custom lifestyle images of your product in any space",
        tags: [
            { text: "Scene Creator" },
            { text: "Lifestyle Image Generator" }
        ]
    },
    {
        img: "https://d48f7equ64qjl.cloudfront.net/static_files/homepage_assets/feature_4.png",
        top_text: "Your powerful all-in-one platform",
        title: "Add finalized assets to your storefront in a few clicks",
        tags: [
            { text: "API Integration" }
        ]
    }
];




const ProductCreationProcess = () => {

    return (
        <>
            {deviceType() == 'desktop' ? <WebVersion /> :
                <MobileVersion />}
        </>
    );
};

const WebVersion = () => {
    const sectionRef = useRef(null);
    const slideRefs = useRef([]);
    slideRefs.current = [];

    const addToRefs = (el) => {
        if (el && !slideRefs.current.includes(el)) {
            slideRefs.current.push(el);
        }
    };


    useLayoutEffect(() => {
        const ctx = gsap.context(() => {
            const slides = slideRefs.current;
            const totalSlides = slides.length;

            // Initial visibility setup
            slides.forEach((el, i) => {
                gsap.set(el, { autoAlpha: i === 0 ? 1 : 0 });
            });

            // Pin and snap the section
            ScrollTrigger.create({
                trigger: sectionRef.current,
                start: "top top",
                end: () => `+=${totalSlides * window.innerHeight}`,
                pin: true,
                snap: {
                    snapTo: 1 / (totalSlides - 1),
                    duration: 0.3,
                    ease: "power1.inOut",
                },
                onUpdate: (self) => {
                    const progress = self.progress;
                    const index = Math.round(progress * (totalSlides - 1));

                    slides.forEach((el, i) => {
                        gsap.to(el, {
                            autoAlpha: i === index ? 1 : 0,
                            duration: 0.5,
                            overwrite: "auto",
                        });
                    });
                },
            });
        }, sectionRef);

        return () => ctx.revert();
    }, []);


    return (
        <Content className="d-none d-lg-block">
            <Row className="product-creation-section-wrapper">
                <Col span={24} className="pd-0">
                    <div className="fade-scroll-section" ref={sectionRef}>
                        {product_creation_flow.map((item, i) => (
                            <div
                                key={i}
                                ref={addToRefs}
                                className="fade-slide full-screen-section">
                                <Row
                                    className="wrapper-padding direction-flex roww justify-in-start" >
                                    <Col span={12} className="direction-flex justify-in-start">
                                        <img
                                            src={item.img}
                                            className="creation-img-dim"
                                            alt={item.title}
                                        />
                                    </Col>
                                    <Col span={12}>
                                        <div className="manrope f-16 w-500 lh-24 mb-24 black-085 text-left">
                                            {item.top_text}
                                        </div>
                                        <div className="manrope f-50 w-600 lh-60 mb-20 black-085 text-left">
                                            {item.title}
                                        </div>
                                        <div className="justify-in-start">
                                            {item.tags.map((tag, j) =>
                                                tag.link ? (
                                                    <a
                                                        key={j}
                                                        href={tag.link_src}
                                                        className="manrope f-14 w-700 lh-22 blue justify-in-start ml-16"
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        {tag.text} <ArrowRightOutlined className="ml-8" />
                                                    </a>
                                                ) : (
                                                    <div key={j} className="ai-creator-container direction-flex roww mr-8">
                                                        <span className="align-self-center justify-in-center">
                                                            {tag.ai && (
                                                                <img src="/ai_creator/ai-creator-twinkle.svg" className="mr-6 ai-icon-dim" />
                                                            )}
                                                            <span className={`manrope f-14 lh-22 ${tag.ai ? `w-700 blue-gradient` : `w-500 blue`}`}>
                                                                {tag.text}
                                                            </span>
                                                            {tag.beta && (
                                                                <span className="manrope f-10 w-700 lh-20 blue beta-text ml-2">BETA</span>
                                                            )}
                                                        </span>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        ))}
                    </div>
                </Col>
                <ToolsInfo />
            </Row>
        </Content>
    );
};

const MobileVersion = () => {
    const sectionRef = useRef(null);
    const slideRefs = useRef([]);
    slideRefs.current = [];

    const addToRefs = (el) => {
        if (el && !slideRefs.current.includes(el)) {
            slideRefs.current.push(el);
        }
    };

    useEffect(() => {
        const ctx = gsap.context(() => {
            const slides = slideRefs.current;
            const totalSlides = slides.length;

            // Initial visibility setup
            slides.forEach((el, i) => {
                gsap.set(el, { autoAlpha: i === 0 ? 1 : 0 });
            });

            // Pin and snap the section
            ScrollTrigger.create({
                trigger: sectionRef.current,
                start: "top top",
                end: () => `+=${totalSlides * window.innerHeight}`,
                pin: true,
                snap: {
                    snapTo: 1 / (totalSlides - 1),
                    duration: 0.2,
                    ease: "power1.inOut",
                    inertia: false
                },
                onUpdate: (self) => {
                    const progress = self.progress;
                    const index = Math.round(progress * (totalSlides - 1));

                    slides.forEach((el, i) => {
                        gsap.to(el, {
                            autoAlpha: i === index ? 1 : 0,
                            duration: 0.2,
                            overwrite: true,
                        });
                    });
                },
            });
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <Content className="d-lg-none">
            <Row className="product-creation-section-wrapper">
                <Col span={24} className={`pd-0`}>
                    <div ref={sectionRef} className="fade-scroll-section">
                        {product_creation_flow.map((item, i) => (
                            <Row key={i}
                                ref={addToRefs}
                                className="wrapper-padding mobile-settings" >
                                <Col span={24} className="direction-flex justify-in-center mb-32">
                                    <img
                                        src={item.img}
                                        className="creation-img-dim"
                                        alt={item.title}
                                    />
                                </Col>
                                <Col span={24}>
                                    <div className="manrope f-16 w-500 lh-24 mb-24 black-085 text-center">
                                        {item.top_text}
                                    </div>
                                    <div className="manrope f-30 w-600 lh-40 mb-24 black-085 text-center">
                                        {item.title}
                                    </div>
                                    <div className="justify-in-center">
                                        {item.tags.map((tag, j) =>
                                            tag.link ? (
                                                <a
                                                    key={j}
                                                    href={tag.link_src}
                                                    className="manrope clamp-w-width f-14 w-700 lh-22 blue justify-in-start ml-16"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    {tag.text} <ArrowRightOutlined className="ml-8" />
                                                </a>
                                            ) : (
                                                <div key={j} className="ai-creator-container direction-flex roww mr-8">
                                                    <span className="align-self-center justify-in-center">
                                                        {tag.ai && (
                                                            <img src="/ai_creator/ai-creator-twinkle.svg" className="mr-6 ai-icon-dim" />
                                                        )}
                                                        <span className={`manrope clamp-w-width f-14 w-500 lh-22 ${tag.ai ? `w-700 blue-gradient` : `blue`}`}>
                                                            {tag.text}
                                                        </span>
                                                        {tag.beta && (
                                                            <span className="manrope clamp-w-width f-10 w-700 lh-20 blue beta-text ml-2">BETA</span>
                                                        )}
                                                    </span>
                                                </div>
                                            )
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        ))}
                    </div>
                </Col>
            </Row>
            <ToolsInfoMobile />
        </Content>
    );
}


export default ProductCreationProcess;
